import React, {memo, useEffect, useRef, useState} from 'react';
import {MenuItem, Tooltip} from '@mui/material';
import {ReactComponent as MoreSvg} from "../../../../../assets/icons/more.svg";
import {useNavigate} from 'react-router-dom';
import {ReactComponent as PinSvg} from "../../../../../assets/icons/pin.svg";
import {ReactComponent as EditSvg} from "../../../../../assets/icons/edit.svg";
import {ReactComponent as TrashSvg} from "../../../../../assets/icons/trash.svg";
import {useTranslation} from 'react-i18next';
import {
    ActiveOptionsButton,
    ConversationItemWrapper,
    OptionsButton,
    StyledButton,
    StyledInput
} from './ConversationItem.styled';
import {ConversationResponse} from '../../../../../models/models';
import {useAppConstants} from '../../../../../config/appConstants';
import {parseError} from '../../../../../errors/http.service';
import {useSnackbar} from '../../../../../contexts/SnackbarContext';
import {StyledMenu} from "../../Sidebar.styled";
import ConfirmationDialog from "../../../../dialogs/ConfirmationDialog";
import {conversationService} from "../../../../../services/conversation.service";

interface ConversationItemProps {
    convo: ConversationResponse;
    currentChatId: number | null;
    isOpen: boolean;
    onRefresh: () => Promise<void>;
}

const ConversationItem = ({
    convo, currentChatId, isOpen, onRefresh,
}: ConversationItemProps) => {
    const {DEFAULT_CONVERSATION_TITLE_PREFIX} = useAppConstants()
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    const inputRef = useRef<HTMLInputElement>(null);
    const isSavingRef = useRef<boolean>(false);

    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const isActive = currentChatId === convo.id;
    const displayTitle = convo.title || `${DEFAULT_CONVERSATION_TITLE_PREFIX}${convo.id}`;

    const handleOpenConversation = () => {
        if (isEditing) return;
        navigate(`/chat/${convo.id}`);
    };

    const handleOpenActionMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
    };

    const handleCloseActionMenu = () => setMenuAnchorEl(null);

    const handleOpenTitleEdition = () => {
        handleCloseActionMenu();
        setEditTitle(displayTitle);
        setIsEditing(true);
    };

    const handleTitleUpdate = async () => {
        if (isSavingRef.current) return;
        if (!editTitle.trim()) {
            setIsEditing(false);
            return;
        }

        isSavingRef.current = true;
        setIsEditing(false);

        try {
            await conversationService.updateConversationTitle(String(convo.id), editTitle.trim());
            await onRefresh();
        } catch (error) {
            console.error('Error updating title:', error);
            showSnackbar(parseError(error), "error");
        } finally {
            isSavingRef.current = false;
        }
    };


    const handleInputTouched = () => {
        if (editTitle.trim()) {
            handleTitleUpdate();
        } else {
            setIsEditing(false);
            setEditTitle('');
        }
    };

    const handleTogglePin = async () => {
        handleCloseActionMenu();
        try {
            if (convo.pinned) {
                await conversationService.unpinConversation(String(convo.id));
            } else {
                await conversationService.pinConversation(String(convo.id));
            }
            await onRefresh();
        } catch (error) {
            showSnackbar(parseError(error), 'error');
        }
    };

    const handleDelete = async () => {
        try {
            await conversationService.deleteConversation(String(convo.id));
            await onRefresh();
            setIsDeleteDialogOpen(false);
            showSnackbar(t('sidebar.conversationDeleted'), 'success');
        } catch (error) {
            showSnackbar(parseError(error), 'error');
        }
    };

    useEffect(() => {
        if (isEditing && inputRef.current) {
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }, [isEditing]);

    return (
        <ConversationItemWrapper $active={isActive}>
            <StyledButton
                open={isOpen}
                onClick={handleOpenConversation}
                onDoubleClick={handleOpenTitleEdition}
            >
                {isEditing ? (
                    <StyledInput
                        ref={inputRef}
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onBlur={handleInputTouched}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleTitleUpdate();
                            if (e.key === 'Escape') {
                                setIsEditing(false);
                                setEditTitle('');
                            }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    />
                ) : (
                    <>
                        {convo.pinned && (
                            <div
                                style={{
                                    marginRight: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <PinSvg
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                        color: currentChatId === convo.id ? 'white' : '#474747',
                                    }}
                                />
                            </div>
                        )}
                        <span>{displayTitle}</span>
                    </>
                )}
            </StyledButton>

            <Tooltip title={t('sidebar.showConversationOptions')}>
                {isActive ? (
                    <ActiveOptionsButton onClick={handleOpenActionMenu}>
                        <MoreSvg/>
                    </ActiveOptionsButton>
                ) : (
                    <OptionsButton onClick={handleOpenActionMenu}>
                        <MoreSvg/>
                    </OptionsButton>
                )}
            </Tooltip>


            <StyledMenu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseActionMenu}
            >
                <MenuItem onClick={handleTogglePin}>
                    <PinSvg style={{marginRight: '14px', width: '19px', height: '19px', color: '#474747'}}/>

                    <span style={{
                        color: '#474747',
                        fontSize: '14px'
                    }}>
                        {convo?.pinned ? t('sidebar.unpin') : t('sidebar.pin')}
                    </span>
                </MenuItem>

                <MenuItem onClick={handleOpenTitleEdition}>
                    <EditSvg style={{marginRight: '14px', width: '19px', height: '19px', color: '#474747'}}/>

                    <span style={{color: '#474747', fontSize: '14px'}}>
                        {t('sidebar.changeName')}
                    </span>
                </MenuItem>

                <MenuItem onClick={(e) => {
                    e.stopPropagation();
                    handleCloseActionMenu();
                    setIsDeleteDialogOpen(true); }}
                >
                    <TrashSvg style={{marginRight: '14px', width: '19px', height: '19px', color: '#474747'}}/>

                    <span style={{color: '#474747', fontSize: '14px'}}>
                        {t('sidebar.delete')}
                    </span>
                </MenuItem>
            </StyledMenu>

            <ConfirmationDialog
                open={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleDelete}
                title={t('sidebar.deleteConfirmationTitle')}
                message={
                    <>
                        {t('sidebar.deleteConfirmationMessage')} &ldquo;
                        {displayTitle}&rdquo;?
                    </>
                }
                confirmButtonText={t('sidebar.deleteButton') as string}
                cancelButtonText={t('sidebar.cancelButton') as string}
            />
        </ConversationItemWrapper>
    );
};

const arePropsEqual = (prev: ConversationItemProps, next: ConversationItemProps) =>
    prev.convo.id === next.convo.id &&
    prev.convo.title === next.convo.title &&
    prev.convo.pinned === next.convo.pinned &&
    prev.currentChatId === next.currentChatId &&
    prev.isOpen === next.isOpen;

export default memo(ConversationItem, arePropsEqual);